import type { BackfillDto, BatchResponse, JobDto } from '@zinc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Panel, smallButton, tableCell, when } from './parts';

const JOB_LABELS: Record<string, string> = {
  rerate: 'Re-rate library',
  'retry-needs-plugin': 'Retry needs-a-plugin puzzles',
  'reimport-fog': 'Re-import fog puzzles',
  'lone-cages': 'One-cell cages to givens',
  'fog-givens': 'Givens out of the fog',
  'fog-recheck': 'Generated fog puzzles checked again',
  'line-colours': 'Lines in their own colours',
  'merge-duplicates': 'Duplicate puzzles merged',
  'rule-batch': 'Rule reading batch',
  'backfill-chunk': 'Backfill chunk',
};

function summary(job: JobDto): string {
  if (job.state === 'paused') return job.error ?? 'Paused';
  if (job.state === 'failed') return job.error ?? 'Failed';
  if (job.state !== 'done')
    return job.state === 'running' ? `Running · ${Math.round(job.progress * 100)}%` : 'Queued';
  return job.result ? JSON.stringify(job.result).replace(/[{}"]/g, '').replace(/,/g, ', ') : 'Done';
}

function Backfill({ b, act }: { b: BackfillDto; act: (name: string, action: string) => void }) {
  const actions =
    b.state === 'idle'
      ? [['start', 'Start']]
      : b.state === 'running'
        ? [['pause', 'Pause']]
        : b.state === 'paused'
          ? [['resume', 'Resume']]
          : [['restart', 'Run again']];
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-line-soft py-2 text-[14px]">
      <span className="min-w-[200px] flex-1 font-semibold">{b.label}</span>
      <span className="text-muted tabular-nums">
        {b.state} · {b.checked.toLocaleString('en-GB')} checked · {b.imported} puzzles · last chunk{' '}
        {when(b.lastChunkAt)}
      </span>
      {b.lastError && <span className="text-[13px] text-mistake">{b.lastError}</span>}
      {actions.map(([action, label]) => (
        <button
          key={action}
          type="button"
          className={smallButton}
          onClick={() => act(b.name, action!)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** Admin → Batch jobs: backfills (a chunk each night at 02:00, within the API cap), re-rate,
 * retry needs-a-plugin, rule reading batches. */
export function AdminBatchPage() {
  const client = useQueryClient();
  const batch = useQuery({
    queryKey: ['admin', 'batch'],
    queryFn: () => api<BatchResponse>('/api/admin/batch'),
    refetchInterval: 5000,
  });
  const refresh = () => void client.invalidateQueries({ queryKey: ['admin'] });
  const act = useMutation({
    mutationFn: (v: { name: string; action: string }) =>
      api(`/api/admin/backfills/${v.name}/${v.action}`, { method: 'POST' }),
    onSuccess: refresh,
  });
  const start = useMutation({
    mutationFn: (path: string) => api(path, { method: 'POST' }),
    onSuccess: refresh,
  });
  return (
    <>
      <h1 className="m-0 text-[26px] font-bold">Batch jobs</h1>
      <Panel title="Backfills">
        <p className="m-0 text-[13px] text-muted">
          A chunk runs each night at 02:00 (Belgrade). Rule reading for a chunk goes out as one
          batch at half price, within the monthly cap.
        </p>
        {(batch.data?.backfills ?? []).map((b) => (
          <Backfill key={b.name} b={b} act={(name, action) => act.mutate({ name, action })} />
        ))}
      </Panel>
      <Panel title="Library">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={smallButton}
            onClick={() => start.mutate('/api/admin/rerate')}
          >
            Re-rate library
          </button>
          <button
            type="button"
            className={smallButton}
            onClick={() => start.mutate('/api/admin/retry-needs-plugin')}
          >
            Retry needs-a-plugin puzzles
          </button>
        </div>
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-muted">
                <th className={`${tableCell} font-semibold`}>Job</th>
                <th className={`${tableCell} font-semibold`}>Started</th>
                <th className={`${tableCell} font-semibold`}>State</th>
              </tr>
            </thead>
            <tbody>
              {(batch.data?.jobs ?? [])
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                .map((j) => (
                  <tr key={j.id}>
                    <td className={`${tableCell} font-semibold`}>{JOB_LABELS[j.type] ?? j.type}</td>
                    <td className={tableCell}>{when(j.createdAt)}</td>
                    <td className={`${tableCell} ${j.state === 'failed' ? 'text-mistake' : ''}`}>
                      {summary(j)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
